import React, { useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MenuAtom, { MenuItem, MenuHandle } from '../../atoms/menu/menu';
import { resolveLabel } from '../../../utils/resolveLabel';
import type { LocalizedString } from 'types/context';
import type { FilterExpression } from 'types/filters';
import './ContextSelectorMenuMolecule.scss';

export interface ContextOption {
  systemName: string;
  name: LocalizedString;
  description?: LocalizedString;
  icon?: string;
  level0PropertyFilters?: FilterExpression[];
  allLevelPropertyFilters?: FilterExpression[][];
}

export interface ContextSelectorMenuMoleculeProps {
  contexts: ContextOption[];
  selectedContext: string | null;
  onSelect: (systemName: string | null) => void;
  loading?: boolean;
  className?: string;
}

const ContextSelectorMenuMolecule: React.FC<ContextSelectorMenuMoleculeProps> = ({
  contexts,
  selectedContext,
  onSelect,
  loading = false,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const menuRef = useRef<MenuHandle>(null);

  const sortedContexts = useMemo(() => {
    return [...contexts].sort((a, b) => {
      const nameA = resolveLabel(a.name, i18n.language);
      const nameB = resolveLabel(b.name, i18n.language);
      return nameA.localeCompare(nameB);
    });
  }, [contexts, i18n.language]);

  const menuItems: MenuItem[] = useMemo(() => {
    if (loading) {
      return [
        {
          template: () => (
            <div className="context-selector-menu__loading">
              <i className="pi pi-spin pi-spinner" />
              <span>{t('contextSelector.loading')}</span>
            </div>
          ),
          disabled: true,
        },
      ];
    }

    const globalItem: MenuItem = {
      template: () => (
        <div
          className={`context-selector-menu__menu-item ${
            !selectedContext ? 'context-selector-menu__menu-item--selected' : ''
          }`}
          onClick={() => onSelect(null)}
          role="menuitem"
        >
          <i className="pi pi-globe context-selector-menu__icon" />
          <div className="context-selector-menu__item-text">
            <span className="context-selector-menu__label">
              {t('contextSelector.global')}
            </span>
            <span className="context-selector-menu__description">
              {t('contextSelector.globalDescription')}
            </span>
          </div>
          {!selectedContext && (
            <i className="pi pi-check context-selector-menu__check" />
          )}
        </div>
      ),
      command: () => onSelect(null),
    };

    if (sortedContexts.length === 0) {
      return [
        globalItem,
        { separator: true },
        {
          template: () => (
            <div className="context-selector-menu__empty">
              <span>{t('contextSelector.noContexts')}</span>
            </div>
          ),
          disabled: true,
        },
      ];
    }

    const contextItems: MenuItem[] = sortedContexts.map((ctx) => {
      const isSelected = selectedContext === ctx.systemName;
      const label = resolveLabel(ctx.name, i18n.language);

      return {
        template: () => (
          <div
            className={`context-selector-menu__menu-item ${
              isSelected ? 'context-selector-menu__menu-item--selected' : ''
            }`}
            onClick={() => onSelect(ctx.systemName)}
            role="menuitem"
          >
            <i
              className={`pi ${ctx.icon || 'pi-filter'} context-selector-menu__icon`}
            />
            <div className="context-selector-menu__item-text">
              <span className="context-selector-menu__label">{label}</span>
            </div>
            {isSelected && (
              <i className="pi pi-check context-selector-menu__check" />
            )}
          </div>
        ),
        command: () => onSelect(ctx.systemName),
      };
    });

    return [globalItem, { separator: true }, ...contextItems];
  }, [loading, sortedContexts, selectedContext, onSelect, t, i18n.language]);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    menuRef.current?.toggle(event);
  };

  const selectedLabel = useMemo(() => {
    if (!selectedContext) return t('contextSelector.global');
    const ctx = contexts.find((c) => c.systemName === selectedContext);
    return ctx ? resolveLabel(ctx.name, i18n.language) : t('contextSelector.global');
  }, [selectedContext, contexts, t, i18n.language]);

  const selectedIcon = useMemo(() => {
    if (!selectedContext) return 'pi-globe';
    const ctx = contexts.find((c) => c.systemName === selectedContext);
    return ctx?.icon || 'pi-filter';
  }, [selectedContext, contexts]);

  return (
    <div className={`context-selector-menu ${className ?? ''}`}>
      <button
        className="context-selector-menu__trigger"
        onClick={handleToggle}
        type="button"
        aria-label={t('contextSelector.title')}
        aria-haspopup="true"
      >
        <i className={`pi ${selectedIcon} context-selector-menu__trigger-icon`} />
        <span className="context-selector-menu__trigger-label">{selectedLabel}</span>
        <i className="pi pi-chevron-down context-selector-menu__trigger-chevron" />
      </button>
      <MenuAtom
        model={menuItems}
        popup
        ref={menuRef}
        appendTo={document.body}
        className="context-selector-menu__popup"
      />
    </div>
  );
};

export default ContextSelectorMenuMolecule;
